import { useMemo } from 'react';

import { ListType, LIST_TYPES } from 'constants/listTypes';

import {
  PaginatedSelect,
  BasePaginatedSelectProps,
  PaginatedSelectQueryProps,
  PaginatedDefaultProps,
  PaginatedSingleProps,
  PaginatedMultiProps,
  ParseMultipleItems,
  ParseSingleItem,
} from '../PaginatedSelect';
import {
  List,
  fetchListsQuery,
  parseListsResponse,
  fetchListQuery,
  parseListResponse,
} from './ListSelect.queries';

export const LIST_SELECT_PLACEHOLDER = {
  SINGLE: 'Select a List',
  MULTI: 'Select Lists',
};

export const DEFAULT_LIST_TYPES = [LIST_TYPES.DYNAMIC, LIST_TYPES.STANDARD, LIST_TYPES.INTERNAL];

export type FetchListParams = {
  listType?: ListType | ListType[];
  search?: string;
};

export const getListSelectFetchItemsVariables = ({
  listType = DEFAULT_LIST_TYPES,
  search,
}: FetchListParams = {}) => ({
  search,
  filterInfo: { listType },
});

export type ListSelectProps<TItem extends List = List> = BasePaginatedSelectProps &
  Partial<PaginatedSelectQueryProps<TItem>> &
  (PaginatedDefaultProps | PaginatedSingleProps | PaginatedMultiProps) & {
    /** The list types to show */
    listType?: ListType[];
  };

export function ListSelect<TItem extends List = List>({
  listType = DEFAULT_LIST_TYPES,
  ...props
}: ListSelectProps<TItem>) {
  const fetchVariables = useMemo(() => getListSelectFetchItemsVariables({ listType }), [listType]);

  return (
    <PaginatedSelect
      fetchAllItemsQuery={fetchListsQuery}
      fetchAllItemsVariables={fetchVariables}
      fetchAllItemsParser={parseListsResponse as ParseMultipleItems<TItem>}
      fetchSelectedItemQuery={fetchListQuery}
      fetchSelectedItemParser={parseListResponse as ParseSingleItem<TItem>}
      placeholder={props?.isMulti ? LIST_SELECT_PLACEHOLDER.MULTI : LIST_SELECT_PLACEHOLDER.SINGLE}
      hasError={props?.isError}
      {...props}
    />
  );
}

export default ListSelect;
